import axios from 'axios';
import { getJobApiCall, postJobApiCall, putJobApiCall, patchJobApiCall, deleteJobApiCall } from '../../utils/jobRequest';
import { handle403Logout } from '../../utils/request';
import { returnErrors } from './errorAction';
import {
  SET_ONBOARDING_FLOWS,
  SET_ONBOARDING_FORMS,
  SET_ONBOARDING_RUNS,
  SET_ONBOARDING_DOCUMENTS,
  SET_ONBOARDING_CHECKLIST_TEMPLATES,
  SET_ONBOARDING_HR_ASSIGNEES,
  SET_ONBOARDING_SETTINGS,
  SET_ONBOARDING_RUN_DETAIL,
  SET_ONBOARDING_PORTAL,
} from '../Types/types';

// ─── HR ASSIGNEES ─────────────────────────────────────────────────────────────
export const fetchHrAssignees = () => async (dispatch) => {
  try {
    const response = await getJobApiCall('onboarding/hr-assignees');
    if (response?.status === 200) {
      dispatch({ type: SET_ONBOARDING_HR_ASSIGNEES, payload: response.data });
      return response.data;
    } else {
      returnErrors(dispatch, response?.data?.message, response?.status);
    }
  } catch (error) {
    handle403Logout(error);
    returnErrors(dispatch, error?.response?.data?.message, error?.response?.status);
  }
};

// ─── FLOWS ────────────────────────────────────────────────────────────────────
export const fetchFlows = (page = 1, pageSize = 10, params = '') => async (dispatch) => {
  try {
    let query = `page=${page}&page_size=${pageSize}`;
    if (params) query += `&${params}`;
    const response = await getJobApiCall(`onboarding/flows?${query}`);
    if (response?.status === 200) {
      dispatch({ type: SET_ONBOARDING_FLOWS, payload: response.data });
      return response.data;
    } else {
      returnErrors(dispatch, response?.data?.message, response?.status);
    }
  } catch (error) {
    handle403Logout(error);
    returnErrors(dispatch, error?.response?.data?.message, error?.response?.status);
  }
};

export const fetchFlowById = (flowId) => async (dispatch) => {
  try {
    const response = await getJobApiCall(`onboarding/flows/${flowId}`);
    if (response?.status === 200) {
      return response.data;
    } else {
      returnErrors(dispatch, response?.data?.message, response?.status);
    }
  } catch (error) {
    handle403Logout(error);
    returnErrors(dispatch, error?.response?.data?.message, error?.response?.status);
  }
};

export const createFlow = (payload) => async (dispatch) => {
  try {
    const response = await postJobApiCall('onboarding/flows', payload);
    if (response?.status === 200 || response?.status === 201) {
      return response.data;
    } else {
      returnErrors(dispatch, response?.data?.message, response?.status);
    }
  } catch (error) {
    handle403Logout(error);
    returnErrors(dispatch, error?.response?.data?.message, error?.response?.status);
  }
};

export const updateFlow = (flowId, payload) => async (dispatch) => {
  try {
    const response = await patchJobApiCall(`onboarding/flows/${flowId}`, payload);
    if (response?.status === 200) {
      return response.data;
    } else {
      returnErrors(dispatch, response?.data?.message, response?.status);
    }
  } catch (error) {
    handle403Logout(error);
    returnErrors(dispatch, error?.response?.data?.message, error?.response?.status);
  }
};

export const deleteFlow = (flowId) => async (dispatch) => {
  try {
    const response = await deleteJobApiCall(`onboarding/flows/${flowId}`);
    if (response?.status === 200 || response?.status === 204) {
      return response.data;
    } else {
      returnErrors(dispatch, response?.data?.message, response?.status);
    }
  } catch (error) {
    handle403Logout(error);
    returnErrors(dispatch, error?.response?.data?.message, error?.response?.status);
  }
};

export const fetchFlowsSummary = () => async (dispatch) => {
  try {
    const response = await getJobApiCall('onboarding/flows/summary');
    if (response?.status === 200) {
      return response.data;
    }
  } catch (error) {
    handle403Logout(error);
  }
};

// ─── FORMS ────────────────────────────────────────────────────────────────────
export const fetchForms = (page = 1, pageSize = 10, params = '') => async (dispatch) => {
  try {
    let query = `page=${page}&page_size=${pageSize}`;
    if (params) query += `&${params}`;
    const response = await getJobApiCall(`onboarding/forms?${query}`);
    if (response?.status === 200) {
      dispatch({ type: SET_ONBOARDING_FORMS, payload: response.data });
      return response.data;
    } else {
      returnErrors(dispatch, response?.data?.message, response?.status);
    }
  } catch (error) {
    handle403Logout(error);
    returnErrors(dispatch, error?.response?.data?.message, error?.response?.status);
  }
};

export const createForm = (payload) => async (dispatch) => {
  try {
    const response = await postJobApiCall('onboarding/forms', payload);
    if (response?.status === 200 || response?.status === 201) {
      return response.data;
    } else {
      const message = response?.data?.message || 'Failed to save form.';
      const detail = response?.data?.detail;
      returnErrors(dispatch, message, response?.status);
      return { __error: true, message, detail };
    }
  } catch (error) {
    handle403Logout(error);
    const message = error?.response?.data?.message || 'Failed to save form.';
    const detail = error?.response?.data?.detail;
    returnErrors(dispatch, message, error?.response?.status);
    return { __error: true, message, detail };
  }
};

export const updateForm = (formId, payload) => async (dispatch) => {
  try {
    const response = await patchJobApiCall(`onboarding/forms/${formId}`, payload);
    if (response?.status === 200) {
      return response.data;
    } else {
      const message = response?.data?.message || 'Failed to save form.';
      const detail = response?.data?.detail;
      returnErrors(dispatch, message, response?.status);
      return { __error: true, message, detail };
    }
  } catch (error) {
    handle403Logout(error);
    const message = error?.response?.data?.message || 'Failed to save form.';
    const detail = error?.response?.data?.detail;
    returnErrors(dispatch, message, error?.response?.status);
    return { __error: true, message, detail };
  }
};

export const deleteForm = (formId) => async (dispatch) => {
  try {
    const response = await deleteJobApiCall(`onboarding/forms/${formId}`);
    if (response?.status === 200 || response?.status === 204) {
      return response.data;
    } else {
      returnErrors(dispatch, response?.data?.message, response?.status);
    }
  } catch (error) {
    handle403Logout(error);
    returnErrors(dispatch, error?.response?.data?.message, error?.response?.status);
  }
};

// ─── DOCUMENTS ────────────────────────────────────────────────────────────────
export const fetchDocuments = (bucket = 'org', page = 1, pageSize = 10) => async (dispatch) => {
  try {
    let query = `page=${page}&page_size=${pageSize}`;
    if (bucket) query += `&bucket=${bucket}`;
    const response = await getJobApiCall(`onboarding/documents?${query}`);
    if (response?.status === 200) {
      dispatch({ type: SET_ONBOARDING_DOCUMENTS, payload: response.data });
      return response.data;
    } else {
      returnErrors(dispatch, response?.data?.message, response?.status);
    }
  } catch (error) {
    handle403Logout(error);
    returnErrors(dispatch, error?.response?.data?.message, error?.response?.status);
  }
};

export const uploadDocument = (formData) => async (dispatch) => {
  try {
    const response = await postJobApiCall('onboarding/documents/upload', formData);
    if (response?.status === 200 || response?.status === 201) {
      return { success: true, data: response.data };
    } else {
      const message = response?.response?.data?.message || response?.data?.message || 'Upload failed';
      const detail = response?.response?.data?.detail || response?.data?.detail;
      returnErrors(dispatch, message, response?.response?.status || response?.status);
      return { success: false, message, detail };
    }
  } catch (error) {
    handle403Logout(error);
    const message = error?.response?.data?.message || error?.message || 'Upload failed';
    const detail = error?.response?.data?.detail;
    returnErrors(dispatch, message, error?.response?.status);
    return { success: false, message, detail };
  }
};

export const deleteDocument = (docId) => async (dispatch) => {
  try {
    const response = await deleteJobApiCall(`onboarding/documents/${docId}`);
    if (response?.status === 200 || response?.status === 204) {
      return response.data;
    } else {
      returnErrors(dispatch, response?.data?.message, response?.status);
    }
  } catch (error) {
    handle403Logout(error);
    returnErrors(dispatch, error?.response?.data?.message, error?.response?.status);
  }
};

// ─── CHECKLIST TEMPLATES ──────────────────────────────────────────────────────
export const fetchChecklistTemplates = (params = '') => async (dispatch) => {
  try {
    const response = await getJobApiCall(`onboarding/checklists${params ? `?${params}` : ''}`);
    if (response?.status === 200) {
      dispatch({ type: SET_ONBOARDING_CHECKLIST_TEMPLATES, payload: response.data });
      return response.data;
    } else {
      returnErrors(dispatch, response?.data?.message, response?.status);
    }
  } catch (error) {
    handle403Logout(error);
    returnErrors(dispatch, error?.response?.data?.message, error?.response?.status);
  }
};

export const createChecklistTemplate = (payload) => async (dispatch) => {
  try {
    const response = await postJobApiCall('onboarding/checklists', payload);
    if (response?.status === 200 || response?.status === 201) {
      return response.data;
    } else {
      returnErrors(dispatch, response?.data?.message, response?.status);
    }
  } catch (error) {
    handle403Logout(error);
    returnErrors(dispatch, error?.response?.data?.message, error?.response?.status);
  }
};

export const updateChecklistTemplate = (checklistId, payload) => async (dispatch) => {
  try {
    const response = await putJobApiCall(`onboarding/checklists/${checklistId}`, payload);
    if (response?.status === 200) {
      return response.data;
    } else {
      returnErrors(dispatch, response?.data?.message, response?.status);
    }
  } catch (error) {
    handle403Logout(error);
    returnErrors(dispatch, error?.response?.data?.message, error?.response?.status);
  }
};

export const deleteChecklistTemplate = (checklistId) => async (dispatch) => {
  try {
    const response = await deleteJobApiCall(`onboarding/checklists/${checklistId}`);
    if (response?.status === 200 || response?.status === 204) {
      return response.data;
    } else {
      returnErrors(dispatch, response?.data?.message, response?.status);
    }
  } catch (error) {
    handle403Logout(error);
    returnErrors(dispatch, error?.response?.data?.message, error?.response?.status);
  }
};

// ─── ACTIVE ONBOARDING RUNS ───────────────────────────────────────────────────
export const fetchOnboardingRuns = (page = 1, pageSize = 10, params = '') => async (dispatch) => {
  try {
    let query = `page=${page}&page_size=${pageSize}`;
    if (params) query += `&${params}`;
    const response = await getJobApiCall(`onboarding/runs?${query}`);
    if (response?.status === 200) {
      dispatch({ type: SET_ONBOARDING_RUNS, payload: response.data });
      return response.data;
    } else {
      returnErrors(dispatch, response?.data?.message, response?.status);
    }
  } catch (error) {
    handle403Logout(error);
    returnErrors(dispatch, error?.response?.data?.message, error?.response?.status);
  }
};

export const fetchRunsSummary = (params = '') => async (dispatch) => {
  try {
    const response = await getJobApiCall(`onboarding/runs/summary${params ? `?${params}` : ''}`);
    if (response?.status === 200) {
      return response.data;
    }
  } catch (error) {
    handle403Logout(error);
  }
};

export const initiateOnboarding = (payload) => async (dispatch) => {
  try {
    const response = await postJobApiCall('onboarding/runs', payload);
    if (response?.status === 200 || response?.status === 201) {
      return response.data;
    } else {
      returnErrors(dispatch, response?.data?.message, response?.status);
    }
  } catch (error) {
    handle403Logout(error);
    returnErrors(dispatch, error?.response?.data?.message, error?.response?.status);
  }
};

export const fetchRunById = (runId) => async (dispatch) => {
  try {
    const response = await getJobApiCall(`onboarding/runs/${runId}`);
    if (response?.status === 200) {
      dispatch({ type: SET_ONBOARDING_RUN_DETAIL, payload: response.data });
      return response.data;
    } else {
      returnErrors(dispatch, response?.data?.message, response?.status);
    }
  } catch (error) {
    handle403Logout(error);
    returnErrors(dispatch, error?.response?.data?.message, error?.response?.status);
  }
};

export const deleteRun = (runId, payload) => async (dispatch) => {
  try {
    const response = await deleteJobApiCall(`onboarding/runs/${runId}`, payload);
    if (response?.status === 200 || response?.status === 204) {
      return response.data;
    } else {
      returnErrors(dispatch, response?.data?.message, response?.status);
    }
  } catch (error) {
    handle403Logout(error);
    returnErrors(dispatch, error?.response?.data?.message, error?.response?.status);
  }
};

export const resendPortalInvite = (runId) => async (dispatch) => {
  try {
    const response = await postJobApiCall(`onboarding/runs/${runId}/resend`, {});
    if (response?.status === 200) {
      return response.data;
    } else {
      returnErrors(dispatch, response?.data?.message, response?.status);
    }
  } catch (error) {
    handle403Logout(error);
    returnErrors(dispatch, error?.response?.data?.message, error?.response?.status);
  }
};

export const reinvokeRun = (runId) => async (dispatch) => {
  try {
    const response = await postJobApiCall(`onboarding/runs/${runId}/invoke`, {
      resend_email: true,
      skip_invite_email: false,
      skip_otp: false,
    });
    if (response?.status === 200 || response?.status === 201) {
      return response.data;
    } else {
      returnErrors(dispatch, response?.data?.message, response?.status);
    }
  } catch (error) {
    handle403Logout(error);
    returnErrors(dispatch, error?.response?.data?.message, error?.response?.status);
  }
};

export const getRecruiterRunView = (runId) => async (dispatch) => {
  try {
    const response = await getJobApiCall(`onboarding/runs/${runId}/recruiter-view`);
    if (response?.status === 200) {
      return response.data;
    } else {
      returnErrors(dispatch, response?.data?.message, response?.status);
    }
  } catch (error) {
    handle403Logout(error);
    returnErrors(dispatch, error?.response?.data?.message, error?.response?.status);
  }
};

export const getRunPortalLink = (runId) => async (dispatch) => {
  try {
    const response = await getJobApiCall(`onboarding/runs/${runId}/portal-link`);
    if (response?.status === 200) {
      return response.data;
    } else {
      returnErrors(dispatch, response?.data?.message, response?.status);
    }
  } catch (error) {
    handle403Logout(error);
    returnErrors(dispatch, error?.response?.data?.message, error?.response?.status);
  }
};

export const requestRunChanges = (runId, payload) => async (dispatch) => {
  try {
    const response = await postJobApiCall(`onboarding/runs/${runId}/request-changes`, payload);
    if (response?.status === 200 || response?.status === 201) return response.data;
    returnErrors(dispatch, response?.data?.message, response?.status);
  } catch (error) {
    handle403Logout(error);
    returnErrors(dispatch, error?.response?.data?.message, error?.response?.status);
  }
};

export const completeRunReview = (runId) => async (dispatch) => {
  try {
    const response = await postJobApiCall(`onboarding/runs/${runId}/complete-review`, {});
    if (response?.status === 200 || response?.status === 201) return response.data;
    returnErrors(dispatch, response?.data?.message, response?.status);
  } catch (error) {
    handle403Logout(error);
    returnErrors(dispatch, error?.response?.data?.message, error?.response?.status);
  }
};

// ─── CANDIDATE PORTAL ─────────────────────────────────────────────────────────

// No-auth POST — OTP endpoints require no Authorization header
const noAuthPost = async (url, body) => {
  try {
    return await axios.post(process.env.REACT_APP_AI_API_KEY + url, body);
  } catch (error) {
    return error.response || { status: 500, data: { message: error.message || 'Request failed' } };
  }
};

// Portal session calls — use the portal access_token, NOT the HR Bearer token
const portalSessionCall = async (method, url, sessionToken, body = null) => {
  const fullUrl = process.env.REACT_APP_AI_API_KEY + url;
  const config = { headers: { Authorization: `Bearer ${sessionToken}` } };
  try {
    if (method === 'GET') return await axios.get(fullUrl, config);
    if (method === 'PATCH') return await axios.patch(fullUrl, body, config);
    return await axios.post(fullUrl, body, config);
  } catch (error) {
    return error.response || { status: 500, data: { message: error.message || 'Request failed' } };
  }
};

export const requestPortalOtp = (invite_token) => async (dispatch) => {
  const response = await noAuthPost('onboarding/candidate-portal/request-otp', { invite_token });
  if (response?.status === 200) return { success: true, data: response.data?.data ?? response.data };
  const detail = response?.data?.detail;
  const message = detail || response?.data?.message || 'Failed to send OTP.';
  return { success: false, status: response?.status, message, detail };
};

export const verifyPortalOtp = (invite_token, otp) => async (dispatch) => {
  const response = await noAuthPost('onboarding/candidate-portal/verify-otp', { invite_token, otp });
  if (response?.status === 200) return { success: true, data: response.data?.data ?? response.data };
  const detail = response?.data?.detail;
  const message = detail || response?.data?.message || 'Invalid OTP. Please try again.';
  return { success: false, status: response?.status, message, detail };
};

export const loadCandidatePortal = (sessionToken) => async (dispatch) => {
  const response = await portalSessionCall('GET', 'onboarding/candidate-portal', sessionToken);
  if (response?.status === 200) {
    const payload = response.data?.data ?? response.data;
    dispatch({ type: SET_ONBOARDING_PORTAL, payload });
    return { success: true, data: payload };
  }
  return { success: false, message: response?.data?.message || 'Failed to load portal.' };
};

export const patchPortalProgress = (sessionToken, payload) => async (dispatch) => {
  const response = await portalSessionCall('PATCH', 'onboarding/candidate-portal/progress', sessionToken, payload);
  if (response?.status === 200) return { success: true, data: response.data };
  return { success: false, message: response?.data?.message || 'Failed to save progress.' };
};

export const submitPortalFormAnswers = (sessionToken, formId, payload) => async (dispatch) => {
  const response = await portalSessionCall('POST', `onboarding/candidate-portal/forms/${formId}/responses`, sessionToken, payload);
  if (response?.status === 200 || response?.status === 201) return { success: true, data: response.data };
  return { success: false, message: response?.data?.message || 'Failed to submit form.' };
};

export const finalSubmitPortal = (sessionToken, payload) => async (dispatch) => {
  const response = await portalSessionCall('POST', 'onboarding/candidate-portal/submit', sessionToken, payload);
  if (response?.status === 200) return { success: true, data: response.data };
  return { success: false, message: response?.data?.message || 'Failed to submit portal.' };
};

export const uploadPortalFormFile = (sessionToken, formId, fieldIndex, formData) => async () => {
  const fullUrl = process.env.REACT_APP_AI_API_KEY + `onboarding/candidate-portal/forms/${formId}/fields/${fieldIndex}/upload`;
  try {
    const response = await axios.post(fullUrl, formData, {
      headers: { Authorization: `Bearer ${sessionToken}` },
    });
    if (response?.status === 200 || response?.status === 201) return { success: true, data: response.data };
    return { success: false, message: 'Upload failed' };
  } catch (error) {
    return { success: false, message: error?.response?.data?.message || 'Upload failed' };
  }
};

// ─── ONBOARDING SETTINGS ──────────────────────────────────────────────────────
export const fetchOnboardingSettings = () => async (dispatch) => {
  try {
    const response = await getJobApiCall('onboarding/settings');
    if (response?.status === 200) {
      dispatch({ type: SET_ONBOARDING_SETTINGS, payload: response.data });
      return response.data;
    } else {
      returnErrors(dispatch, response?.data?.message, response?.status);
    }
  } catch (error) {
    handle403Logout(error);
    returnErrors(dispatch, error?.response?.data?.message, error?.response?.status);
  }
};

export const saveOnboardingSettings = (payload) => async (dispatch) => {
  try {
    const response = await putJobApiCall('onboarding/settings', payload);
    if (response?.status === 200) {
      dispatch({ type: SET_ONBOARDING_SETTINGS, payload: response.data });
      return response.data;
    } else {
      returnErrors(dispatch, response?.data?.message, response?.status);
    }
  } catch (error) {
    handle403Logout(error);
    returnErrors(dispatch, error?.response?.data?.message, error?.response?.status);
  }
};

// ─── INVOKE FLOW (Test / Direct) ─────────────────────────────────────────────
export const invokeFlow = (payload) => async (dispatch) => {
  try {
    const response = await postJobApiCall('onboarding/invoke', payload);
    if (response?.status === 200 || response?.status === 201) {
      return response.data;
    } else {
      returnErrors(dispatch, response?.data?.message, response?.status);
    }
  } catch (error) {
    handle403Logout(error);
    returnErrors(dispatch, error?.response?.data?.message, error?.response?.status);
  }
};

// ─── JOB CANDIDATE PREVIEW ────────────────────────────────────────────────────
export const fetchJobCandidate = (jobId, candidateId) => async (dispatch) => {
  try {
    const response = await getJobApiCall(`jobs/${jobId}/job-candidates/${candidateId}`);
    if (response?.status === 200) {
      return response.data;
    } else {
      returnErrors(dispatch, response?.data?.message, response?.status);
    }
  } catch (error) {
    handle403Logout(error);
    returnErrors(dispatch, error?.response?.data?.message, error?.response?.status);
  }
};
